import { useState } from "react";
import type { TerminalOrder } from "../lib/types";
import { canMarkReady, canStartPreparing } from "../lib/orderGrouping";
import { AcceptDialog } from "./AcceptDialog";
import { RejectDialog } from "./RejectDialog";

interface Props {
  order: TerminalOrder;
  busy: boolean;
  onAccept: (orderId: string, prepMinutes: number) => void;
  onReject: (orderId: string, reasonCode: string, comment?: string) => void;
  onPreparing: (orderId: string) => void;
  onReady: (orderId: string) => void;
}

const STATUS_LABEL: Record<string, string> = {
  awaiting_merchant: "Wartet auf Entscheidung",
  merchant_accepted: "Angenommen",
  awaiting_assignment: "Wartet auf Fahrer",
  rider_assigned: "Fahrer zugewiesen",
  rider_accepted: "Fahrer akzeptiert",
  en_route_to_shop: "Fahrer unterwegs",
  at_shop: "Fahrer am Laden",
  picked_up: "Abgeholt",
  en_route_to_point: "Auf dem Weg",
  at_point: "Am ShoppiPoint",
  handed_over: "Übergeben",
  not_collected: "Nicht abgeholt",
  cancelled: "Storniert",
  incident: "Vorfall",
  merchant_rejected: "Abgelehnt",
};

export function OrderCard({ order, busy, onAccept, onReject, onPreparing, onReady }: Props) {
  const [showAccept, setShowAccept] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const isDecision = order.status === "awaiting_merchant";

  return (
    <article className={`order-card ${isDecision ? "order-card--decision" : ""}`} aria-label={`Bestellung ${order.order_number}`}>
      <header className="order-card__head">
        <span className="order-card__num">#{order.order_number}</span>
        <span className="order-card__status" data-status={order.status}>{STATUS_LABEL[order.status] ?? order.status}</span>
      </header>

      <div className="order-card__meta">
        <div>Eingegangen: {formatTime(order.created_at)}</div>
        {order.requested_pickup_at && <div>Abholung: {formatTime(order.requested_pickup_at)}</div>}
        {order.shoppi_point_name && <div>ShoppiPoint: {order.shoppi_point_name}{order.shoppi_point_code ? ` (${order.shoppi_point_code})` : ""}</div>}
        {order.prep_minutes != null && <div>Zubereitung: {order.prep_minutes} min</div>}
      </div>

      {order.items && order.items.length > 0 && (
        <ul className="order-card__items">
          {order.items.map((it, i) => (
            <li key={i}>{it.quantity ? `${it.quantity}× ` : ""}{it.name ?? "Position"}</li>
          ))}
        </ul>
      )}
      {order.customer_note && <p className="order-card__note">Notiz: {order.customer_note}</p>}

      <footer className="order-card__actions">
        {isDecision && (
          <>
            <button className="btn btn--primary" disabled={busy} onClick={() => setShowAccept(true)}>Annehmen</button>
            <button className="btn btn--danger" disabled={busy} onClick={() => setShowReject(true)}>Ablehnen</button>
          </>
        )}
        {canStartPreparing(order) && (
          <button className="btn" disabled={busy} onClick={() => onPreparing(order.order_id)}>Zubereitung starten</button>
        )}
        {canMarkReady(order) && (
          <button className="btn btn--primary" disabled={busy} onClick={() => onReady(order.order_id)}>Bestellung ist bereit</button>
        )}
      </footer>

      {showAccept && (
        <AcceptDialog
          orderNumber={order.order_number}
          busy={busy}
          onCancel={() => setShowAccept(false)}
          onConfirm={(min) => { setShowAccept(false); onAccept(order.order_id, min); }}
        />
      )}
      {showReject && (
        <RejectDialog
          orderNumber={order.order_number}
          busy={busy}
          onCancel={() => setShowReject(false)}
          onConfirm={(reason, comment) => { setShowReject(false); onReject(order.order_id, reason, comment); }}
        />
      )}
    </article>
  );
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
